import { combineEpics, ofType } from 'redux-observable';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Action } from '../actions';
import {
    closeCommonPriceEditor,
    updateCommonPriceEditorIsInProgressData
} from '../actions/commonPriceEditor';
import {
    deleteEditCellData,
    resetGridData,
    updateEditCellData
} from '../actions/grid-screen';
import {
    onUpdatePhrase,
    SAVE_PHRASES_MASS_COMMON_PRICE_EDITOR,
    UPDATE_PHRASE_BID_REMOTE,
    UPDATE_PHRASE_REMOTE,
    updatePhraseBid,
    UpdatePhraseBidRemoteAction,
    UpdatePhraseRemoteAction
} from '../actions/phrases-grid';
import {
    mapPhraseBidChangeToApiFormat,
    mapPhraseChangeToApiFormat
} from '../mappers/phrase/phrase';
import { awaitCommonData } from '../operators';
import { selectGridSourceParams } from '../selectors/gridSourceParams/gridSourceParams';
import { showPhrasesActionNoticeMessage } from '../service/gridItemsActionsHelpers/gridItemsActionsHelpers';
import { DnaEpic } from '../typings/epic';
import { setBidsMutation } from '../typings/graphql-grid';
import { PhrasesAction } from '../typings/grid';

const updatePhraseRemoteEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_PHRASE_REMOTE),
        mergeMap(
            (action: UpdatePhraseRemoteAction): Observable<Action> => {
                const state = store$.value;
                const sourceParams = selectGridSourceParams(state);

                const { login, view } = sourceParams;
                const keywordUpdateItems = mapPhraseChangeToApiFormat(
                    action.phrase
                );
                const cellKey = {
                    ...action.cellKeyPartial,
                    view: view
                };

                return from(
                    deps.phrasesMutationExecutor.updateKeywords(
                        { keywordUpdateItems },
                        login
                    )
                ).pipe(
                    mergeMap(
                        (): Observable<Action> =>
                            of(
                                deleteEditCellData(cellKey),
                                onUpdatePhrase(action.phrase)
                            )
                    ),
                    catchError(error =>
                        of(
                            updateEditCellData(cellKey, {
                                isSaving: false,
                                error: error.getMessage()
                            })
                        )
                    )
                );
            }
        )
    );
};

const updatePhraseBidEpic: DnaEpic = (action$, store$, deps) => {
    return action$.pipe(
        ofType(UPDATE_PHRASE_BID_REMOTE),
        mergeMap(
            (action: UpdatePhraseBidRemoteAction): Observable<Action> => {
                const state = store$.value;
                const { login, view } = selectGridSourceParams(state);
                const cellKey = {
                    ...action.cellKeyPartial,
                    view
                };

                return from(
                    deps.pricesWizardMutationExecutor.setBids(
                        mapPhraseBidChangeToApiFormat(action.phrase),
                        login
                    )
                ).pipe(
                    mergeMap(
                        (): Observable<Action> =>
                            of(
                                deleteEditCellData(cellKey),
                                updatePhraseBid(action.phrase)
                            )
                    ),
                    catchError(error =>
                        of(
                            updateEditCellData(cellKey, {
                                isSaving: false,
                                error: error.getMessage()
                            })
                        )
                    )
                );
            }
        )
    );
};

export const updatePhraseMassCommonPriceEpic: DnaEpic = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(SAVE_PHRASES_MASS_COMMON_PRICE_EDITOR),
        awaitCommonData(action$, store$),
        mergeMap(
            (): Observable<Action> => {
                const state = store$.value;
                const sourceParams = selectGridSourceParams(state);
                const {
                    grid: { selectionInfo },
                    commonPriceEditor: { commonPriceValue, errors }
                } = state;
                const phrasesIds = selectionInfo.phrases;

                if (errors.length) {
                    return of(updateCommonPriceEditorIsInProgressData(false));
                }

                return from(
                    deps.pricesWizardMutationExecutor.setBids(
                        {
                            showConditionIds: phrasesIds,
                            exactPriceContext: String(commonPriceValue)
                        },
                        sourceParams.login
                    )
                ).pipe(
                    mergeMap(
                        (response: setBidsMutation): Observable<Action> => {
                            const { showConditionIds } = response.setBids;
                            const successCount = showConditionIds.length;

                            if (!response || !successCount) {
                                return throwError('error');
                            }

                            showPhrasesActionNoticeMessage(
                                PhrasesAction.setPhrasesCommonPriceEditor,
                                successCount,
                                phrasesIds.length
                            );

                            return of(
                                resetGridData(),
                                closeCommonPriceEditor()
                            );
                        }
                    ),
                    catchError(() => {
                        showPhrasesActionNoticeMessage(
                            PhrasesAction.setPhrasesCommonPriceEditor,
                            0,
                            1
                        );

                        return of(closeCommonPriceEditor());
                    })
                );
            }
        )
    );
};

export const updatePhraseEpic = combineEpics(
    updatePhraseRemoteEpic,
    updatePhraseBidEpic,
    updatePhraseMassCommonPriceEpic
);
